import { on } from '@ngrx/store';

import { getGraphqlErrorExtension } from 'src/app/utilities/errors.util';

import { BlueSageRefundLegActions } from '../bluesage-refund-leg.actions';
import { toMinorUnits } from '../bluesage.util';

import { BlueSageRefundDialogActions } from './bluesage-refund-dialog.actions';
import {
    BlueSageRefundDialogState,
    REFUND_REASON_NEEDING_DETAIL,
    blueSageRefundDialogInitialState,
} from './bluesage-refund-dialog.state';

/**
 * The exact figure the rail says a full refund takes.
 *
 * `409 partial_refund_unavailable` carries `full_refund_amount` in MAJOR units,
 * which is why this converts. Without the figure the dialog can still offer a
 * full refund; it just cannot name it.
 */
function fullRefundFigureFrom(error: unknown): number | undefined {
    const major = getGraphqlErrorExtension(error, 'full_refund_amount');

    return typeof major === 'number' && Number.isFinite(major)
        ? toMinorUnits(major)
        : undefined;
}

export const blueSageRefundDialogReducers = [

    on(
        BlueSageRefundDialogActions.refundMenuItemClicked,
        (
            state: BlueSageRefundDialogState,
            {
                transactionId, originalMinor, refundableMinor, channel, cardLast4,
                provider, saleDifferentialMinor, salePaidMinor, saleTipMinor,
            },
        ): BlueSageRefundDialogState => ({
            ...blueSageRefundDialogInitialState,
            open: true,
            transactionId,
            originalMinor,
            refundableMinor,
            channel,
            cardLast4,
            provider,
            saleDifferentialMinor,
            salePaidMinor,
            saleTipMinor,
            // Requirement: pre-filled to the full REFUNDABLE amount, not the full
            // original. After a partial, the original is `partially_refunded` and
            // more can still be refunded — but not the whole original again. The
            // common case is then one click with no typing.
            amountMinor: refundableMinor,
        }),
    ),

    on(
        BlueSageRefundDialogActions.amountChanged,
        (state: BlueSageRefundDialogState, { amountMinor }): BlueSageRefundDialogState => ({
            ...state,
            amountMinor,
            // Editing the amount is the operator answering the refusal, so the
            // refusal should not stay on screen contradicting them.
            error: undefined,
            fullRefundOnlyMinor: undefined,
        }),
    ),

    on(
        BlueSageRefundDialogActions.reasonCategoryChanged,
        (state: BlueSageRefundDialogState, { reasonCategory }): BlueSageRefundDialogState => ({
            ...state,
            reasonCategory,
            // Moving off `Other` drops words that were written to explain it.
            // Keeping them would file "Damage — wrong card charged" against a
            // category that contradicts the sentence.
            reasonDetail: reasonCategory === REFUND_REASON_NEEDING_DETAIL ? state.reasonDetail : '',
        }),
    ),

    on(
        BlueSageRefundDialogActions.reasonDetailChanged,
        (state: BlueSageRefundDialogState, { reasonDetail }): BlueSageRefundDialogState =>
            ({ ...state, reasonDetail }),
    ),

    on(
        BlueSageRefundDialogActions.refundTipToggled,
        (state: BlueSageRefundDialogState, { refundTip }): BlueSageRefundDialogState => ({
            ...state,
            refundTip,
        }),
    ),

    on(
        BlueSageRefundDialogActions.refundFullInsteadButtonClicked,
        (state: BlueSageRefundDialogState): BlueSageRefundDialogState => ({
            ...state,
            // The figure the rail itself named, falling back to what we already
            // believe is refundable when the refusal did not carry one.
            amountMinor: state.fullRefundOnlyMinor ?? state.refundableMinor,
            error: undefined,
            fullRefundOnlyMinor: undefined,
        }),
    ),

    on(
        BlueSageRefundDialogActions.confirmButtonClicked,
        (state: BlueSageRefundDialogState): BlueSageRefundDialogState => ({
            ...state,
            submitting: true,
            error: undefined,
        }),
    ),

    on(
        BlueSageRefundDialogActions.refundSuccess,
        (
            state: BlueSageRefundDialogState,
            { rail, knownLegIds, awaitsConfirmation },
        ): BlueSageRefundDialogState => ({
            ...state,
            submitting: false,
            done: true,
            knownLegIds,
            awaitsConfirmation,
            // Stated AFTER the fact, never predicted: which rail you get depends on
            // whether the device was reachable at that moment.
            resultRail: rail,
        }),
    ),

    on(
        BlueSageRefundDialogActions.refundError,
        (state: BlueSageRefundDialogState, { error }): BlueSageRefundDialogState => ({
            ...state,
            submitting: false,
            // Already mapped from `body.code` server-side. The code list is OPEN by
            // contract, so an unknown code arrives here as prose rather than a
            // crash — and never as a raw code on screen.
            error: error?.message,
            fullRefundOnlyMinor: fullRefundFigureFrom(error),
        }),
    ),

    // Requirement: a refusal that arrives AFTER the refund was sent must not
    // strand the operator on a dead end. Nothing moved, so they return to the
    // form exactly as they left it, with BlueSage's reason above it.
    on(
        BlueSageRefundDialogActions.tryAgainButtonClicked,
        (state: BlueSageRefundDialogState, { message }): BlueSageRefundDialogState => ({
            ...state,
            done: false,
            knownLegIds: [],
            awaitsConfirmation: false,
            confirmationOverdue: false,
            error: message,
        }),
    ),

    on(
        BlueSageRefundLegActions.outcomeWindowClosed,
        (state: BlueSageRefundDialogState, { transactionId }): BlueSageRefundDialogState =>
            state.done && state.transactionId === transactionId
                ? { ...state, confirmationOverdue: true }
                : state,
    ),

    on(
        BlueSageRefundDialogActions.dialogClosed,
        (): BlueSageRefundDialogState => blueSageRefundDialogInitialState,
    ),
];
